import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Play, MapPin, Phone, Clock, ArrowRight, Car, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import VideoModal from "@/components/VideoModal";
import SEO from "@/components/SEO";

const FALLBACK_GALLERY = [
  "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/products/783935758/5194593293.jpg",
  "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/products/795385667/5364186985.jpg",
  "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/products/815157341/5576012257.jpg",
  "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/4807217352.jpg",
  "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/products/783634108/5190841459.jpg",
  "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/products/783935758/5194593295.jpg",
];

export default function Showroom() {
  const { pick } = useLang();
  const [site, setSite] = useState({});
  const [modal, setModal] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
    api.get("/showroom").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  const heroPoster = items.find((it) => it.image_url)?.image_url || FALLBACK_GALLERY[0];

  const wa = site.whatsapp ? `https://wa.me/${site.whatsapp.replace(/[^\d]/g,"")}?text=${encodeURIComponent("Bonjour KV, je souhaite prendre rendez-vous au showroom.")}` : "#";
  const directions = "https://www.google.com/maps/dir/?api=1&destination=Chauss%C3%A9e+de+Waterloo+242+1640+Rhode-Saint-Gen%C3%A8se";

  return (
    <div data-testid="showroom-page" className="bg-[#0D0D0D] min-h-screen">
      <SEO title="Le showroom · Chaussée de Waterloo 242 · Rhode-Saint-Genèse" description="Découvrez notre showroom d'exposition à Rhode-Saint-Genèse. Uniquement sur rendez-vous. Porsche modernes et classiques, véhicules sportifs de prestige." />
      {/* HERO with video + play CTA */}
      <section className="relative min-h-[85vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          {site.showroom_video_url && (
            <video key={site.showroom_video_url} autoPlay muted loop playsInline poster={heroPoster} className="w-full h-full object-cover img-fade">
              <source src={site.showroom_video_url} type="video/mp4" />
            </video>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/60 to-[#0D0D0D]/30" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pb-24 pt-32 w-full">
          <p className="text-xs uppercase tracking-[0.4em] text-[#C8A75A] fade-up">Le showroom · Rhode-Saint-Genèse</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light text-white max-w-4xl fade-up fade-up-delay-1">
            Venez pousser la porte.
          </h1>
          <p className="text-white/70 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed fade-up fade-up-delay-2">
            Un espace calme, sur rendez-vous, où chaque véhicule s'admire — et où l'on prend le temps de parler passion.
          </p>
          <div className="flex flex-wrap gap-4 mt-10 fade-up fade-up-delay-3">
            <button
              data-testid="showroom-play"
              onClick={() => setModal(true)}
              className="bg-[#C8A75A] text-black hover:bg-white transition-colors duration-300 font-medium px-8 py-4 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-2"
            >
              <Play className="w-4 h-4" fill="currentColor" /> Visite virtuelle
            </button>
            <a href={directions} target="_blank" rel="noreferrer" data-testid="showroom-directions" className="border border-white/20 text-white hover:bg-white/5 transition-colors duration-300 px-8 py-4 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Itinéraire
            </a>
          </div>
        </div>
      </section>

      {/* Info card */}
      <section className="relative -mt-10 z-20 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="bg-[#1E1E1E] border border-[#C8A75A]/30 rounded-md shadow-[0_20px_60px_rgba(0,0,0,0.6)] p-6 md:p-8 grid md:grid-cols-3 gap-6">
          <InfoLine icon={MapPin} label="Adresse" value="Chaussée de Waterloo 242 · 1640 Rhode-Saint-Genèse" />
          <InfoLine icon={Clock} label="Horaires" value={pick(site.hours) || "Uniquement sur rendez-vous"} />
          <InfoLine icon={Phone} label="Téléphone" value={site.phone || "+32 486 52 19 06"} href={site.phone ? `tel:${site.phone.replace(/\s+/g,"")}` : null} />
        </div>
      </section>

      {/* Gallery — objets exposés (admin) OU fallback showroom */}
      <section className="py-32 lg:py-44">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">{items.length ? "Objets exposés" : "Galerie"}</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white mb-4">
            {items.length ? "Produits & œuvres." : "Un aperçu du lieu."}
          </h2>
          {items.length > 0 && (
            <p className="text-white/50 max-w-xl mb-14 leading-relaxed">
              Sélection curatée d'objets, éditions limitées et œuvres d'art visibles dans notre showroom de Rhode-Saint-Genèse.
            </p>
          )}

          {items.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-auto">
              {items.map((it) => (
                <ShowroomCard key={it.id} item={it} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {FALLBACK_GALLERY.map((src, i) => (
                <div key={i} className={`relative rounded-md overflow-hidden bg-[#1E1E1E] ${i === 0 ? "lg:col-span-2 lg:row-span-2 aspect-[4/3] lg:aspect-auto" : "aspect-[4/3]"}`}>
                  <img src={src} alt={`Showroom ${i+1}`} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Map + CTA */}
      <section className="py-32 bg-[#1E1E1E]/30 border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div className="aspect-[16/10] rounded-md overflow-hidden border border-white/10">
            <iframe
              src={site.map_embed || "https://www.google.com/maps?q=Chauss%C3%A9e+de+Waterloo+242,+1640+Rhode-Saint-Gen%C3%A8se&output=embed"}
              width="100%" height="100%" style={{ border: 0, filter: "grayscale(0.6) invert(0.9) contrast(0.85)" }}
              loading="lazy" title="showroom-map"
            />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Nous rendre visite</p>
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-light text-white mb-6">Sur rendez-vous, 7 jours sur 7.</h3>
            <p className="text-white/70 leading-relaxed mb-8">
              Que ce soit pour découvrir un véhicule, discuter d'un projet de vente ou d'un stage simulateur — nous prenons le temps.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href={wa} target="_blank" rel="noreferrer" className="bg-[#25D366] text-black hover:bg-white transition-colors px-6 py-3 rounded-md text-xs uppercase tracking-widest inline-flex items-center gap-2 font-medium">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <Link to="/contact" className="bg-[#C8A75A] text-black hover:bg-white transition-colors px-6 py-3 rounded-md text-xs uppercase tracking-widest inline-flex items-center gap-2 font-medium">
                Prendre rendez-vous <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/stock" className="border border-white/20 text-white hover:bg-white/5 transition-colors px-6 py-3 rounded-md text-xs uppercase tracking-widest inline-flex items-center gap-2">
                <Car className="w-4 h-4" /> Voir le stock
              </Link>
            </div>
          </div>
        </div>
      </section>

      <VideoModal open={modal} onClose={() => setModal(false)} src={site.showroom_video_url || ""} poster={heroPoster} />
    </div>
  );
}

function ShowroomCard({ item }) {
  const [ratio, setRatio] = useState(0.75); // 3:4 par défaut (portrait)
  const [loaded, setLoaded] = useState(false);
  const isWide = loaded && ratio > 1.4; // triptyque / panoramique

  const onLoad = (e) => {
    const img = e.currentTarget;
    if (img.naturalWidth && img.naturalHeight) {
      setRatio(img.naturalWidth / img.naturalHeight);
      setLoaded(true);
    }
  };

  return (
    <article
      data-testid={`sr-item-${item.id}`}
      className={`group ${isWide ? "sm:col-span-2 lg:col-span-2" : ""}`}
    >
      <div
        className="relative rounded-md overflow-hidden bg-[#0D0D0D] border border-white/5 flex items-center justify-center"
        style={{ aspectRatio: loaded ? `${ratio}` : "3 / 4", minHeight: "260px" }}
      >
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.title}
            loading="lazy"
            onLoad={onLoad}
            className="max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-[1.03] transition-transform duration-[900ms]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20 text-xs uppercase tracking-widest">Aucune photo</div>
        )}
        <span className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm text-[9px] uppercase tracking-[0.3em] text-[#C8A75A] px-2.5 py-1 rounded-sm border border-[#C8A75A]/30">
          {item.category === "art" ? "Œuvre d'art" : "Produit dérivé"}
        </span>
      </div>
      <div className="pt-5">
        <h3 className="font-display text-xl md:text-2xl text-white leading-tight">{item.title}</h3>
        {item.description && <p className="text-white/60 text-sm mt-2 leading-relaxed line-clamp-3">{item.description}</p>}
        <p className="mt-4 text-[#C8A75A] tracking-wide">
          {item.price
            ? new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(item.price)
            : (item.price_label || "Prix sur demande")}
        </p>
      </div>
    </article>
  );
}

function InfoLine({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-4">
      <span className="w-10 h-10 rounded-full bg-[#C8A75A]/10 text-[#C8A75A] flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" strokeWidth={1.4} />
      </span>
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</p>
        <p className="text-white text-sm mt-1 leading-snug">{value}</p>
      </div>
    </div>
  );
  return href ? <a href={href} className="hover:opacity-80 transition-opacity">{inner}</a> : inner;
}
